import { Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { type PartialSkill } from "~/services/skills";

export interface SkillRowProps {
  skill: PartialSkill;
  canEdit: boolean;
  onView?: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const SkillRow = ({ skill, canEdit, onView, onEdit, onDelete }: SkillRowProps) => {
  const handleOpen = canEdit ? onEdit : onView;

  return (
    <li
      className={cn(
        "group relative flex items-center gap-3 px-4 py-3",
        "first:rounded-t-lg last:rounded-b-lg",
        handleOpen && "cursor-pointer hover:bg-accent/30"
      )}
    >
      <div className="min-w-0 flex-1">
        {handleOpen ? (
          // The pseudo-element stretches the button over the whole row so the trash button
          // can sit on top of it without being nested inside another button.
          <button
            type="button"
            onClick={handleOpen}
            className="block w-full cursor-pointer text-left after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:rounded-[inherit] focus-visible:after:ring-2 focus-visible:after:ring-ring"
          >
            <span className="block text-sm font-medium">{skill.name}</span>
            {skill.description && (
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {skill.description}
              </span>
            )}
          </button>
        ) : (
          <>
            <p className="text-sm font-medium">{skill.name}</p>
            {skill.description && (
              <p className="mt-0.5 text-xs text-muted-foreground">{skill.description}</p>
            )}
          </>
        )}
      </div>

      {canEdit && (
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "relative size-7 shrink-0 text-muted-foreground hover:text-destructive",
            "opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          )}
          aria-label={`Remove ${skill.name}`}
          onClick={onDelete}
        >
          <Trash2 className="size-4" />
        </Button>
      )}
    </li>
  );
};
